import SaveCancel from 'components/save-cancel';
import React, { useEffect, useState } from 'react';
import styles from 'components/setting-filter-form/styles/SettingFilterForm.module.scss';
import { FilterFormInterface, initialState } from 'components/setting-filter-form/initialState';
import { RuleName, Condition, FilterAction, Clause, Priority } from 'components/setting-filter-form/sections';
import { KiteAlert, KiteCard, KiteProgressIndicator } from '@kite/react-kite';
import { isEqual } from 'lodash';
import useFilterFieldValidatorError from 'hooks/useContactFieldValidatorError';
import BackButton from 'components/common/BackButton';
import { updateFilterThunk } from 'thunks/filter/updateFilterThunk';
import { useAppDispatch, useAppSelector } from 'hooks';
import {
  useUpdateFiltersMutation,
  useCreateMailboxMutation,
  useDeleteMailboxMutation,
  OperatorEnum,
  FieldEnum,
} from 'generated/graphql';
import { updateNavBlocking, updateToastMessage } from 'slices/app';
import { resetFilterApiError } from 'slices/filter';
import { useNavigate } from 'react-router';
import transformFilterForm, { apiToFilter, transformExistingFilters } from 'utils/transformFilterForm';
import { isFilterFormValid } from 'components/setting-filter-form/helpers';
import { createMailboxThunk } from 'thunks/mailbox/createMailboxThunk';
import { Navigate, useParams } from 'react-router-dom';
import { TrackingHook, useTracking } from 'react-tracking';
import useNavBlockingDraft from 'hooks/useNavBlockingDraft';
import { PageNSTranslationFileKeys, usePageTranslation } from 'translation/hooks';
import { ApolloError } from '@apollo/client';
import { DeleteMailboxThunkResponse, deleteMailboxThunk } from 'thunks/mailbox/deleteMailboxThunk';

type NewFolderResult = {
  payload: {
    success: {
      id: string;
      name: string;
      delimiter: string;
    };
    error: ApolloError;
  };
};

interface UpdateFilterProps {
  getFiltersLoading: boolean;
}

const UpdateFilter: React.FC<UpdateFilterProps> = ({ getFiltersLoading }) => {
  const { t } = usePageTranslation('filterRuleFormCopy');
  const [formValues, setFormValues] = useNavBlockingDraft(initialState);
  const [alertDescription, setAlertDescription] = useState<PageNSTranslationFileKeys<'filterRuleFormCopy'> | ''>('');
  const [errors, setErrors] = useFilterFieldValidatorError({});
  const spamFolderId = useAppSelector((state) => state.mailboxCollectionSlice.spamFolderId);
  const apiErrors = useAppSelector((state) => state.mailboxCollectionSlice.apiError);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { filterId } = useParams<{ filterId: string }>();
  const { trackEvent }: TrackingHook = useTracking({
    page: 'settings',
  });

  const filters = useAppSelector((state) => state.filterCollectionSlice.filters) || [];
  const mailboxCollection = useAppSelector((state) => state.mailboxCollectionSlice.mailboxCollection);

  const [updateFiltersHook] = useUpdateFiltersMutation();
  const [createMailboxHook] = useCreateMailboxMutation();
  const [deleteMailboxHook] = useDeleteMailboxMutation();

  if (!filterId) {
    return <Navigate to={'/server-error?component=UpdateFilter'} />;
  }

  useEffect(() => {
    if (apiErrors) {
      setAlertDescription('unableToSaveErr');
    }
  }, [apiErrors]);

  useEffect(() => {
    trackEvent({ event: 'filterRulesEditPageView' });
    const filterIndex = parseInt(filterId);

    const filter = filters[filterIndex];

    if (filter) {
      if (filter.actionForward === 'yes') {
        navigate('/user/setting/filter');
      }
      setFormValues(apiToFilter(filter));
    }
  }, []);

  const deleteFolder = async (folderId: string): Promise<DeleteMailboxThunkResponse> => {
    const deleteFolderResult = await dispatch(
      deleteMailboxThunk({
        deleteMailboxHook,
        payload: {
          deleteMessage: true,
          mailboxId: folderId,
          spamFolderId,
        },
      })
    ).unwrap();
    return deleteFolderResult;
  };

  const updateFormValues = (
    value: FilterFormInterface[keyof FilterFormInterface],
    field: keyof FilterFormInterface
  ): void => {
    setFormValues({ ...formValues, [field]: value });
  };

  const isSaved = async (): Promise<boolean> => {
    setAlertDescription('');
    let newFolderResult: NewFolderResult | undefined;
    if (formValues.moveFolder && formValues.moveFolder === 'createNew') {
      if (
        mailboxCollection?.find(
          (mailbox) => mailbox.name === formValues.newFolder || formValues.newFolder?.toLowerCase() === 'inbox'
        )
      ) {
        trackEvent({ event: 'filterRulesEditError' });
        setAlertDescription('validationErr');
        setErrors({ ...errors, newFolder: 'duplicate' });
        return false;
      }
      newFolderResult = (await dispatch(
        createMailboxThunk({
          payload: {
            mailbox: { mailbox: formValues.newFolder! },
          },
          createMailboxHook,
        })
      )) as NewFolderResult;
    }

    if (!newFolderResult || newFolderResult.payload.success) {
      const filtersToSend = transformExistingFilters(filters);
      const filterIndex = parseInt(filterId);

      const updateFilterResult = await dispatch(
        updateFilterThunk({
          payload: {
            filters: [
              ...filtersToSend.slice(0, filterIndex),
              transformFilterForm(formValues, newFolderResult?.payload.success.id),
              ...filtersToSend.slice(filterIndex + 1),
            ],
          },
          updateFiltersHook,
        })
      ).unwrap();

      if (updateFilterResult.error && newFolderResult?.payload.success) {
        const { name, id } = newFolderResult?.payload.success;

        const deleteFolderResult = await deleteFolder(id);

        if (deleteFolderResult.error) {
          updateFormValues(btoa(name), 'moveFolder');
        }

        trackEvent({ event: 'filterRulesEditError' });
        setAlertDescription('unableToSaveErr');
        return false;
      }

      if (updateFilterResult.success) {
        dispatch(updateNavBlocking(false));
        trackEvent({ event: 'filterRulesEditSuccess' });
        dispatch(updateToastMessage({ message: t('updatedToast'), success: true }));
        dispatch(resetFilterApiError());
        navigate('/user/setting/filter');
        return true;
      }
    }

    trackEvent({ event: 'filterRulesEditError' });
    setAlertDescription('unableToSaveErr');
    return false;
  };

  const isCancelled = (): boolean => {
    trackEvent({ event: 'filterRulesEditCancel' });
    dispatch(updateNavBlocking(false));
    navigate('/user/setting/filter');
    return true;
  };

  const isValidate = (): boolean => {
    trackEvent({ event: 'filterRulesSaveEdit' });
    const [isValid, filterFormErrors] = isFilterFormValid(formValues);
    if (isValid) {
      return true;
    }

    trackEvent({ event: 'filterRulesEditValidationError' });
    setAlertDescription('validationErr');
    setErrors(filterFormErrors);
    return false;
  };

  const onBackClick = (): void => {
    trackEvent({ event: 'filterRulesBackButton' });
    navigate('/user/setting/filter');
  };

  return (
    <div className={styles.filterForm}>
      <BackButton onClick={onBackClick} />
      <KiteCard className={styles.newFilter}>
        <h2 className="kite-type-style--title-6">{t('editRuleTitle')}</h2>
        <p>{t('editRuleDescription')}</p>
        {alertDescription && (
          <KiteAlert
            className={styles.alert}
            type="error"
            description={t(alertDescription)}
            level="page"
            dismissible
            onClose={() => setAlertDescription('')}
          />
        )}
        <form className={styles.formFields}>
          <RuleName
            updateFormValues={updateFormValues}
            values={{ name: formValues.name }}
            errors={errors}
            setErrors={setErrors}
          />
          <Condition
            updateFormValues={updateFormValues}
            values={{
              rules: formValues.rules.map((rule) => {
                return {
                  ...rule,
                  field:
                    rule.field === FieldEnum.Sender && rule.operator === OperatorEnum.HasSuffix
                      ? FieldEnum.From
                      : rule.field,
                };
              }),
            }}
            errors={errors}
            isNew={false}
            setErrors={setErrors}
          />
          <Priority
            updateFormValues={updateFormValues}
            values={{ highPriority: formValues.highPriority, actionPriority: formValues.actionPriority }}
            errors={errors}
            setErrors={setErrors}
          />
          <Clause
            updateFormValues={updateFormValues}
            values={{ clause: formValues.clause }}
            errors={errors}
            setErrors={setErrors}
          />
          <FilterAction
            updateFormValues={updateFormValues}
            values={{
              filterAction: formValues.filterAction,
              moveFolder: formValues.moveFolder,
              newFolder: formValues.newFolder,
            }}
            errors={errors}
            setErrors={setErrors}
          />
        </form>
        <div className={styles.buttonBar}>
          <SaveCancel
            draft={formValues}
            isChanged={() => !isEqual(formValues, initialState)}
            isSaved={isSaved}
            isValidate={isValidate}
            isCancelled={isCancelled}
          />
        </div>
        {getFiltersLoading && <KiteProgressIndicator id={'loading'} useOverlay={true} size="md" />}
      </KiteCard>
    </div>
  );
};

export default UpdateFilter;
